import { Callout, FormLayout, Paragraph, Reservation, ReservationCard } from '@expressable/syntax-ds';
import { ContentfulTherapistCard } from 'components/ContentfulTherapistCard/ContentfulTherapistCard';
import { DirectSignupPreBookErrorDialog } from 'components/Dialog/DirectSignupPreBookErrorDialog';
import { DirectSignupPreBookNoLongerAvailableDialog } from 'components/Dialog/DirectSignupPreBookNoLongerAvailableDialog';
import { Divider } from 'components/Divider';
import { useSignupNavigationGuard } from 'domains/signupDirect/misc/useSignupNavigationGuard';
import { CheckboxInputV2 } from 'components/Forms/inputs/CheckboxInputV2';
import { format } from 'date-fns';
import { AddRecurringAppointmentsDto, addRecurringAppointments } from 'domains/add-recurring-sts/queries';
import { ApiError } from 'domains/api/errors';
import { useSignupDirectCtx } from 'domains/signupDirect/context';
import { DIRECT_SIGNUP_MIXPANEL_EVENTS } from 'domains/signupDirect/misc/mixpanelTracking';
import {
  DEFAULT_TIMEZONE_OPTION,
  TimeZone,
  TimezoneOption,
  getDefaultTimezoneOption,
  timeZoneOptions,
} from 'domains/timezone';
import { DisplayTimezoneProvider } from 'domains/timezone/useDisplayTimezone';
import mixpanel from 'mixpanel-browser';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from 'react-query';
import { getReservationScheduleProps } from 'utils/date';
import { useTrack } from 'utils/hooks/useMixPanel';
import ActionButtons from './ActionButtons';
import PreBookHeader from './Header';
import PreBookSchedulePicker from './PreBookSchedulePicker';
import SessionInstructions from './SessionInstructions';
import { TimezoneSelector } from './TimezoneSelector';
import { createAppointmentData } from './utils/appointmentUtils';
import { isFormValid } from './utils/validationUtils';

// @TODO(juan): extract this to a reusable function. For some reason it errors out if we do, need to investigate whats going on
const trackMixpanelEvent = (eventName: string, properties = {}) => {
  return new Promise(resolve => {
    mixpanel.track(
      eventName,
      properties,
      {
        send_immediately: true,
      },
      () => {
        resolve(true);
      },
    );
  });
};

// INTERFACES ------------------------------------
interface IPreBookState {
  singleSessionOnly: boolean;
  displayTimezone: TimezoneOption | undefined;
  appointments: {
    first: {
      selectedDay?: number;
      timeSlot: Date | undefined;
      date: string;
      timezone: TimeZone;
      therapistName?: string;
    };
    second: {
      selectedDay?: number;
      timeSlot: Date | undefined;
      date: string;
      timezone: TimeZone;
      therapistName?: string;
    };
  };
}

export const PreBookSessions: React.FC = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { track } = useTrack();

  // CONTEXT & VALIDATION ------------------------
  const { filters, evaluationSchedule, speechTherapySessionSchedule, isResumedLead } = useSignupDirectCtx();
  const [showUnavailableDialog, setShowUnavailableDialog] = useState<boolean>(false);
  const [isPostSuccess, setIsPostSuccess] = useState<boolean>(false);

  if (evaluationSchedule?.type !== 'single') throw new Error('Expected evaluation schedule type to be single');

  if (!filters) throw 'Expected filters to be set';
  if (!evaluationSchedule) throw 'Expected evalSelectedTimeSlot to be defined';
  if (!speechTherapySessionSchedule) throw 'Expected speechTherapySessionSchedule to be defined';

  // STATES ------------------------------------
  const [submitError, setSubmitError] = useState<string | null>(null);

  const currentTimezone =
    timeZoneOptions.find(timezone => timezone.resolvedName === evaluationSchedule.timeZone) ?? DEFAULT_TIMEZONE_OPTION;

  const [state, setState] = useState<IPreBookState>({
    singleSessionOnly: false,
    displayTimezone: getDefaultTimezoneOption(currentTimezone),
    appointments: {
      first: {
        selectedDay: undefined,
        timeSlot: undefined,
        date: '',
        timezone: '' as TimeZone,
        therapistName: undefined,
      },
      second: {
        selectedDay: undefined,
        timeSlot: undefined,
        date: '',
        timezone: '' as TimeZone,
        therapistName: undefined,
      },
    },
  });

  // REFS -------------------------------------
  const firstAppointmentRef = useRef<HTMLDivElement>(null);
  const secondAppointmentRef = useRef<HTMLDivElement>(null);

  // EFFECTS ---------------------------------
  useEffect(() => {
    // INIT TRACKER
    track(DIRECT_SIGNUP_MIXPANEL_EVENTS.STEPS.PREBOOK.VIEW, { isResumedLead });

    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  }, []);

  useEffect(() => {
    setState(prev => ({
      ...prev,
      appointments: {
        first: {
          ...prev.appointments.first,
          timeSlot: undefined,
        },
        second: {
          ...prev.appointments.second,
          selectedDay: undefined,
          timeSlot: undefined,
        },
      },
    }));
  }, [state.appointments.first.selectedDay]);

  useEffect(() => {
    if (!state.displayTimezone) {
      setState(prev => ({
        ...prev,
        singleSessionOnly: false,
        appointments: {
          first: {
            selectedDay: undefined,
            timeSlot: undefined,
            date: '',
            timezone: '' as TimeZone,
            therapistName: undefined,
          },
          second: {
            selectedDay: undefined,
            timeSlot: undefined,
            date: '',
            timezone: '' as TimeZone,
            therapistName: undefined,
          },
        },
      }));
    }
  }, [state.displayTimezone]);

  // MUTATIONS -------------------------------
  const { mutate: addAppointments, isLoading: isAddingAppointments } = useMutation<
    unknown,
    ApiError,
    AddRecurringAppointmentsDto
  >(
    async appointment => {
      if (!speechTherapySessionSchedule) throw new Error('No session schedule data available');
      return addRecurringAppointments(
        speechTherapySessionSchedule.clientID,
        appointment,
        speechTherapySessionSchedule.leadId,
      );
    },
    {
      onSuccess: async () => {
        if (state.singleSessionOnly)
          await trackMixpanelEvent(DIRECT_SIGNUP_MIXPANEL_EVENTS.STEPS.PREBOOK.SUBMIT.SCHEDULE_FIRST, {
            isResumedLead,
          });
        else
          await trackMixpanelEvent(DIRECT_SIGNUP_MIXPANEL_EVENTS.STEPS.PREBOOK.SUBMIT.SCHEDULE_BOTH, { isResumedLead });

        setIsPostSuccess(true);
        setSubmitError(null);
        if (speechTherapySessionSchedule?.invitationLink)
          window.location.href = speechTherapySessionSchedule.invitationLink;
      },
      onError: error => {
        const UNAVAILABLE_ERROR_CODES = [
          'BOTH_SESSIONS_UNAVAILABLE',
          'FIRST_SESSION_UNAVAILABLE',
          'SECOND_SESSION_UNAVAILABLE',
        ];

        queryClient.invalidateQueries(['calendar-availability']);

        const errorData = error.originalError?.response?.data as { code: string; message: string };

        switch (errorData?.code) {
          case 'BOTH_SESSIONS_UNAVAILABLE':
            track(DIRECT_SIGNUP_MIXPANEL_EVENTS.STEPS.PREBOOK.ERRORS.AVAILABILITY.BOTH_SESSIONS, { isResumedLead });
            break;
          case 'FIRST_SESSION_UNAVAILABLE':
            track(DIRECT_SIGNUP_MIXPANEL_EVENTS.STEPS.PREBOOK.ERRORS.AVAILABILITY.FIRST_SESSION, { isResumedLead });
            break;
          case 'SECOND_SESSION_UNAVAILABLE':
            track(DIRECT_SIGNUP_MIXPANEL_EVENTS.STEPS.PREBOOK.ERRORS.AVAILABILITY.SECOND_SESSION, { isResumedLead });
            break;
          default:
            track(DIRECT_SIGNUP_MIXPANEL_EVENTS.STEPS.PREBOOK.SKIPPED, { isResumedLead });
            track(DIRECT_SIGNUP_MIXPANEL_EVENTS.STEPS.PREBOOK.ERRORS.GENERIC, { isResumedLead });
        }

        if (UNAVAILABLE_ERROR_CODES.includes(errorData?.code)) {
          setShowUnavailableDialog(true);
          setSubmitError(errorData.code);
        } else {
          setSubmitError(t('signup.direct.steps.prebook.errors.submit'));
        }
      },
    },
  );

  // HANDLERS ------------------------------
  const scrollToRef = (ref: React.RefObject<HTMLElement>) => {
    ref.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const handleFirstAppointmentSubmit = useCallback((date: Date, timezone: TimeZone) => {
    setState(prev => ({
      ...prev,
      appointments: {
        ...prev.appointments,
        first: {
          ...prev.appointments.first,
          date: date.toISOString(),
          timezone,
        },
      },
    }));
  }, []);

  const handleSecondAppointmentSubmit = useCallback((date: Date, timezone: TimeZone) => {
    setState(prev => ({
      ...prev,
      appointments: {
        ...prev.appointments,
        second: {
          ...prev.appointments.second,
          date: date.toISOString(),
          timezone,
        },
      },
    }));
  }, []);

  useSignupNavigationGuard();
  const handleSubmit = useCallback(() => {
    if (!speechTherapySessionSchedule) {
      // eslint-disable-next-line no-console
      console.error('No speechTherapySessionSchedule content available!');
      return;
    }

    const schedulePreference = {
      firstAppointment: {
        date: state.appointments.first.date,
        timezone: state.appointments.first.timezone,
      },
      secondAppointment: {
        date: state.appointments.second.date,
        timezone: state.appointments.second.timezone,
      },
    };

    if (isFormValid(schedulePreference, state.singleSessionOnly, speechTherapySessionSchedule)) {
      const appointmentData = createAppointmentData(
        schedulePreference,
        speechTherapySessionSchedule,
        state.displayTimezone,
        state.singleSessionOnly,
      );
      addAppointments(appointmentData);
    }
  }, [state, speechTherapySessionSchedule, addAppointments]);

  const handleSkip = useCallback(async () => {
    await trackMixpanelEvent(DIRECT_SIGNUP_MIXPANEL_EVENTS.STEPS.PREBOOK.SKIPPED, { isResumedLead });

    if (speechTherapySessionSchedule?.invitationLink) {
      window.location.href = speechTherapySessionSchedule.invitationLink;
    } else {
      setSubmitError(t('signup.direct.steps.prebook.errors.skip'));
    }
  }, [speechTherapySessionSchedule?.invitationLink]);

  // RENDER -------------------------------
  return (
    <>
      <div>
        <PreBookHeader
          title={t('signup.direct.steps.prebook.header.title')}
          subtitle={t('signup.direct.steps.prebook.header.subtitle')}
        />
      </div>
      <SessionInstructions
        title={t('signup.direct.steps.prebook.firstSession.title')}
        description={t('signup.direct.steps.prebook.firstSession.description')}
      />

      <FormLayout>
        <FormLayout.Fields>
          <TimezoneSelector
            displayTimezone={state.displayTimezone}
            setDisplayTimezone={timezone => setState(prev => ({ ...prev, displayTimezone: timezone }))}
          />

          {!state.displayTimezone ? (
            <Paragraph>{t('signup.direct.steps.prebook.noTimezone')}</Paragraph>
          ) : (
            <>
              <DisplayTimezoneProvider>
                <div ref={firstAppointmentRef}>
                  <PreBookSchedulePicker
                    onSubmit={handleFirstAppointmentSubmit}
                    selectedDay={state.appointments.first.selectedDay}
                    timeSlot={state.appointments.first.timeSlot}
                    onDayChange={day => {
                      setState(prev => ({
                        ...prev,
                        appointments: {
                          ...prev.appointments,
                          first: { ...prev.appointments.first, selectedDay: day },
                        },
                      }));
                    }}
                    onTimeSlotChange={(timeSlot, therapistName) => {
                      setState(prev => ({
                        ...prev,
                        appointments: {
                          ...prev.appointments,
                          first: {
                            ...prev.appointments.first,
                            timeSlot,
                            therapistName,
                          },
                        },
                      }));
                    }}
                    evalTimeSlot={evaluationSchedule.timeSlot.time}
                    displayTimezone={state.displayTimezone}
                    calendarID={speechTherapySessionSchedule?.therapistCalendarID}
                    isSecondAppointment={false}
                    isSubmitting={isAddingAppointments}
                    billingType={speechTherapySessionSchedule.billingType}
                  />
                </div>
              </DisplayTimezoneProvider>

              {state.appointments.first.timeSlot && (
                <>
                  <Divider className="my-10" />
                  <SessionInstructions
                    title={t('signup.direct.steps.prebook.secondSession.title')}
                    description={t('signup.direct.steps.prebook.secondSession.description')}
                  />
                  <Callout type="info">{t('signup.direct.steps.prebook.sessionFrequency.callout')}</Callout>
                  <CheckboxInputV2
                    name="single-session-checkbox"
                    id="single-session-checkbox"
                    checked={state.singleSessionOnly}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      setState(prev => ({ ...prev, singleSessionOnly: e.target.checked }))
                    }
                    disabled={false}
                    option={{
                      label: t('signup.direct.steps.prebook.singleSession.checkbox'),
                      value: 'true',
                      startAdornment: true,
                    }}
                  />
                  {!state.singleSessionOnly && (
                    <DisplayTimezoneProvider>
                      <div ref={secondAppointmentRef}>
                        <PreBookSchedulePicker
                          onSubmit={handleSecondAppointmentSubmit}
                          selectedDay={state.appointments.second.selectedDay}
                          disabledDay={state.appointments.first.selectedDay}
                          timeSlot={state.appointments.second.timeSlot}
                          onDayChange={day => {
                            setState(prev => ({
                              ...prev,
                              appointments: {
                                ...prev.appointments,
                                second: { ...prev.appointments.second, selectedDay: day },
                              },
                            }));
                          }}
                          onTimeSlotChange={(timeSlot, therapistName) => {
                            setState(prev => ({
                              ...prev,
                              appointments: {
                                ...prev.appointments,
                                second: {
                                  ...prev.appointments.second,
                                  timeSlot,
                                  therapistName,
                                },
                              },
                            }));
                          }}
                          displayTimezone={state.displayTimezone}
                          evalTimeSlot={evaluationSchedule.timeSlot.time}
                          calendarID={speechTherapySessionSchedule?.therapistCalendarID}
                          isSecondAppointment={true}
                          isSubmitting={isAddingAppointments}
                          billingType={speechTherapySessionSchedule.billingType}
                        />
                      </div>
                    </DisplayTimezoneProvider>
                  )}
                </>
              )}

              {state.appointments.first.timeSlot && (state.appointments.second.timeSlot || state.singleSessionOnly) && (
                <>
                  {state.appointments.first.therapistName && (
                    <ContentfulTherapistCard
                      therapistName={state.appointments.first.therapistName}
                      clinicalAreas={filters.clinicalConcernKeys}
                      stateCode={filters.stateCode}
                    />
                  )}
                  <ReservationCard title={t('signup.direct.steps.prebook.reservations.cardTitle')}>
                    <Reservation
                      title={t('signup.direct.steps.prebook.reservations.sessionTitle')}
                      ctaLabel={t('signup.direct.steps.prebook.reservations.editButton')}
                      onCtaClick={() => {
                        setState(prev => ({
                          ...prev,
                          singleSessionOnly: false,
                          appointments: {
                            ...prev.appointments,
                            first: {
                              ...prev.appointments.first,
                              selectedDay: undefined,
                              timeSlot: undefined,
                              date: '',
                            },
                            second: {
                              ...prev.appointments.second,
                              selectedDay: undefined,
                              timeSlot: undefined,
                              date: '',
                            },
                          },
                        }));
                        scrollToRef(firstAppointmentRef);
                      }}
                      {...getReservationScheduleProps({
                        dateTime: state.appointments.first.timeSlot,
                        therapistName: state.appointments.first.therapistName,
                        pluralize: true,
                        showStarting: true,
                        formatFn: format,
                        timezone: state.displayTimezone?.value,
                      })}
                    />

                    {!state.singleSessionOnly && state.appointments.second.timeSlot && (
                      <Reservation
                        title={t('signup.direct.steps.prebook.reservations.sessionTitle')}
                        ctaLabel={t('signup.direct.steps.prebook.reservations.editButton')}
                        onCtaClick={() => {
                          setState(prev => ({
                            ...prev,
                            appointments: {
                              ...prev.appointments,
                              second: {
                                ...prev.appointments.second,
                                selectedDay: undefined,
                                timeSlot: undefined,
                                date: '',
                              },
                            },
                          }));
                          scrollToRef(secondAppointmentRef);
                        }}
                        {...getReservationScheduleProps({
                          dateTime: state.appointments.second.timeSlot,
                          therapistName: state.appointments.second.therapistName,
                          pluralize: true,
                          showStarting: true,
                          formatFn: format,
                          timezone: state.displayTimezone?.value,
                        })}
                      />
                    )}
                  </ReservationCard>
                </>
              )}
            </>
          )}
        </FormLayout.Fields>

        <FormLayout.CTA>
          <ActionButtons
            value={JSON.stringify({
              firstAppointment: {
                date: state.appointments.first.date,
                timezone: state.appointments.first.timezone,
              },
              secondAppointment: {
                date: state.appointments.second.date,
                timezone: state.appointments.second.timezone,
              },
            })}
            onSubmit={handleSubmit}
            onSkip={handleSkip}
            disabled={
              !isFormValid(
                {
                  firstAppointment: { date: state.appointments.first.date },
                  secondAppointment: { date: state.appointments.second.date },
                },
                state.singleSessionOnly,
                speechTherapySessionSchedule,
              ) ||
              isAddingAppointments ||
              isPostSuccess
            }
            loading={isAddingAppointments || isPostSuccess}
          />
        </FormLayout.CTA>
      </FormLayout>

      <DirectSignupPreBookErrorDialog
        isOpen={!showUnavailableDialog && !!submitError}
        onConfirm={() => {
          window.location.href = speechTherapySessionSchedule.invitationLink;
        }}
        appointment={{
          timeSlot: evaluationSchedule.timeSlot.time,
          therapistName: evaluationSchedule.timeSlot.therapistName,
          timezone: state.displayTimezone?.value as TimeZone,
        }}
        isDismissible={false}
      />

      <DirectSignupPreBookNoLongerAvailableDialog
        isOpen={showUnavailableDialog}
        code={submitError}
        onConfirm={() => {
          setShowUnavailableDialog(false);
          setSubmitError(null);
        }}
        isDismissible={false}
      />
    </>
  );
};
